/* START_MODULE_CONTRACT
 * PURPOSE: React context feature flags: bootstrap from API, ADMIN overrides, isEnabled.
 * SCOPE: FeatureToggleProvider, useFeatureToggle.
 * DEPENDS: M-FE-FEATURE-TOGGLE, M-FE-AUTH, M-FE-API
 * LINKS: M-FE-FEATURE-TOGGLE, V-M-FE-FEATURE-TOGGLE, UC-022
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * FeatureToggleProvider - loads flags, syncs ADMIN overrideAllowed
 * useFeatureToggle - isEnabled, setOverride, clearOverride, reload
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-08-10 - UC-022 FeatureToggleContext.
 * END_CHANGE_SUMMARY
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchFeatureFlags } from '../api/featureToggleApi';
import { useAuth } from './AuthContext';
import {
  FLAG_ENABLE_REGISTRATION,
  readFeatureOverrides,
  resolveIsEnabled,
  setFeatureOverrideAllowed,
  writeFeatureOverrides,
} from '../features/featureToggle/featureToggleStorage';

const FeatureToggleContext = createContext(null);

const DEFAULT_FLAGS = {
  [FLAG_ENABLE_REGISTRATION]: false,
};

/* START_METHOD_FeatureToggleProvider */
/* START_CONTRACT: FeatureToggleProvider
 * PURPOSE: Загрузить флаги, синхронизировать ADMIN gate, предоставить isEnabled/setOverride.
 * INPUTS: { children: ReactNode }
 * OUTPUTS: { JSX.Element }
 * SIDE_EFFECTS: GET /api/features; localStorage; setFeatureOverrideAllowed
 * LINKS: M-FE-FEATURE-TOGGLE, V-M-FE-FEATURE-TOGGLE
 * END_CONTRACT: FeatureToggleProvider
 */
export function FeatureToggleProvider({ children }) {
  const { user } = useAuth();
  const [baseFlags, setBaseFlags] = useState(DEFAULT_FLAGS);
  const [overridesVersion, setOverridesVersion] = useState(0);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'ADMIN';

  useEffect(() => {
    setFeatureOverrideAllowed(isAdmin);
  }, [isAdmin]);

  const reload = useCallback(async (signal) => {
    try {
      const { data } = await fetchFeatureFlags(signal);
      setBaseFlags({
        [FLAG_ENABLE_REGISTRATION]: Boolean(
          data?.enableRegistrationGlobal ?? data?.enableRegistration,
        ),
      });
      console.info('[FeFeatureToggle][reload][BLOCK_FLAGS_LOADED] enableRegistration=', data?.enableRegistration);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return;
      }
      console.warn('[FeFeatureToggle][reload][BLOCK_FLAGS_LOAD_FAIL] keeping defaults');
      setBaseFlags(DEFAULT_FLAGS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    reload(controller.signal);
    return () => controller.abort();
  }, [reload, overridesVersion, isAdmin]);

  /* START_METHOD_isEnabled */
  /* START_CONTRACT: isEnabled
   * PURPOSE: Эффективное значение флага (override для ADMIN иначе base).
   * INPUTS: { flagName: string }
   * OUTPUTS: { boolean }
   * SIDE_EFFECTS: localStorage read when ADMIN
   * LINKS: V-M-FE-FEATURE-TOGGLE
   * END_CONTRACT: isEnabled
   */
  const isEnabled = useCallback(
    (flagName) => resolveIsEnabled(flagName, baseFlags),
    [baseFlags, overridesVersion, isAdmin],
  );
  /* END_METHOD_isEnabled */

  /* START_METHOD_setOverride */
  /* START_CONTRACT: setOverride
   * PURPOSE: Установить локальный оверрайд (только ADMIN).
   * INPUTS: { flagName: string, value: boolean }
   * OUTPUTS: { void }
   * SIDE_EFFECTS: localStorage; bump version; log
   * LINKS: V-M-FE-FEATURE-TOGGLE
   * END_CONTRACT: setOverride
   */
  const setOverride = useCallback((flagName, value) => {
    if (!isAdmin) {
      return;
    }
    const next = { ...readFeatureOverrides(), [flagName]: Boolean(value) };
    writeFeatureOverrides(next);
    // START_BLOCK_OVERRIDE_SET
    console.info('[FeFeatureToggle][setOverride][BLOCK_OVERRIDE_SET]', { flagName, value: Boolean(value) });
    // END_BLOCK_OVERRIDE_SET
    setOverridesVersion((v) => v + 1);
  }, [isAdmin]);
  /* END_METHOD_setOverride */

  /* START_METHOD_clearOverride */
  /* START_CONTRACT: clearOverride
   * PURPOSE: Удалить локальный оверрайд флага или все.
   * INPUTS: { flagName?: string }
   * OUTPUTS: { void }
   * SIDE_EFFECTS: localStorage; bump version; log
   * LINKS: V-M-FE-FEATURE-TOGGLE
   * END_CONTRACT: clearOverride
   */
  const clearOverride = useCallback((flagName) => {
    if (!isAdmin) {
      return;
    }
    if (!flagName) {
      writeFeatureOverrides({});
    } else {
      const next = { ...readFeatureOverrides() };
      delete next[flagName];
      writeFeatureOverrides(next);
    }
    // START_BLOCK_OVERRIDE_CLEAR
    console.info('[FeFeatureToggle][clearOverride][BLOCK_OVERRIDE_CLEAR]', { flagName: flagName ?? 'ALL' });
    // END_BLOCK_OVERRIDE_CLEAR
    setOverridesVersion((v) => v + 1);
  }, [isAdmin]);
  /* END_METHOD_clearOverride */

  const value = useMemo(
    () => ({
      loading,
      baseFlags,
      canOverride: isAdmin,
      overrides: isAdmin ? readFeatureOverrides() : {},
      isEnabled,
      setOverride,
      clearOverride,
      reload: () => reload(),
    }),
    [loading, baseFlags, isAdmin, isEnabled, setOverride, clearOverride, reload, overridesVersion],
  );

  return <FeatureToggleContext.Provider value={value}>{children}</FeatureToggleContext.Provider>;
}
/* END_METHOD_FeatureToggleProvider */

/* START_METHOD_useFeatureToggle */
/* START_CONTRACT: useFeatureToggle
 * PURPOSE: Хук доступа к FeatureToggleContext.
 * INPUTS: нет
 * OUTPUTS: context value
 * SIDE_EFFECTS: throws if outside provider
 * LINKS: M-FE-FEATURE-TOGGLE
 * END_CONTRACT: useFeatureToggle
 */
export function useFeatureToggle() {
  const ctx = useContext(FeatureToggleContext);
  if (!ctx) {
    throw new Error('useFeatureToggle must be used within FeatureToggleProvider');
  }
  return ctx;
}
/* END_METHOD_useFeatureToggle */
